import type { ReactNode } from 'react';
import { Avatar } from '../primitives/Avatar.js';
import { Tooltip } from '../primitives/Tooltip.js';
import { cx } from '../utils/cx.js';
import styles from './StickyNote.module.css';

export type StickyNoteAuthor = {
  firstName: string;
  lastName?: string | null;
  email: string;
  avatarUrl?: string | null;
};

export type StickyNoteProps = {
  children: ReactNode;
  /** Small caption above body (default « Note »). Pass null to hide. */
  label?: string | null;
  author?: StickyNoteAuthor | null;
  className?: string;
};

function authorTooltipLabel(author: StickyNoteAuthor): ReactNode {
  const fullName = [author.firstName, author.lastName?.trim()].filter(Boolean).join(' ').trim();
  return (
    <span className={styles.authorTip}>
      {fullName ? <span className={styles.authorTipName}>{fullName}</span> : null}
      <span className={styles.authorTipEmail}>{author.email}</span>
    </span>
  );
}

/** Auxiliary note callout — aside the main grid, quiet attention, brand chrome. */
export function StickyNote({
  children,
  label = 'Note',
  author,
  className,
}: StickyNoteProps) {
  return (
    <aside className={cx(styles.root, className)} aria-label={label ?? 'Note'}>
      {label ? <span className={styles.label}>{label}</span> : null}
      <div className={styles.body}>{children}</div>
      {author ? (
        <footer className={styles.author}>
          <Avatar
            size="sm"
            label={[author.firstName, author.lastName].filter(Boolean).join(' ') || author.email}
            src={author.avatarUrl}
            className={styles.authorAvatar}
          />
          <Tooltip label={authorTooltipLabel(author)} portal>
            <button type="button" className={styles.authorName}>
              {author.firstName}
            </button>
          </Tooltip>
        </footer>
      ) : null}
    </aside>
  );
}
